import React from "react";
import { useTranslation } from "react-i18next";
import { getLocalAuthHeader, userOrganisationsApi } from "api-client";
import clsx from "clsx";
import { useNavigate } from "react-router";
import ActionMenu from "components/ActionMenu";
import useToast from "hooks/useToast";
import { AxiosResponse } from "axios";
import { organisationStore } from "store/OrganisationStore";
import { observer } from "mobx-react-lite";
import {
  IconAutomation,
  IconLogout,
  IconPencil,
  IconUsers,
} from "@tabler/icons-react";
import useFeatureFlag from "hooks/useFeatureFlag";
import { IS_CIRCULAR_LCA } from "utils";

const OrganisationSettingsMenu = ({
  openMembers,
  openSettings,
  isAdmin,
  multipleAdmin,
}: {
  openMembers: () => void;
  openSettings: () => void;
  isAdmin: boolean;
  multipleAdmin: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clickedOrganisation } = organisationStore;
  const { isFeatureVisible } = useFeatureFlag();
  const toast = useToast();

  const openRulesEditor = () => {
    navigate(`/${organisationStore.clickedOrganisation?.id}/rules-editor`, {
      replace: true,
      state: location.pathname,
    });
  };

  async function leaveOrganisation() {
    const authHeader = getLocalAuthHeader();
    if (!clickedOrganisation?.id || !authHeader) return;
    return await userOrganisationsApi
      .concularApiV1RoutersUserOrganisationsLeaveOrganisation(
        clickedOrganisation.id,
        authHeader
      )
      .then(async (response: AxiosResponse) => {
        if (response.data.success) {
          await organisationStore.getAllOrganisations().then(() => {
            organisationStore.setCurrentOrganisation(undefined);
            navigate("/dashboard", { replace: true });
            toast(
              t("organisationSettings.myOrganisation.leftOrganisationSuccess"),
              "success"
            );
          });
        } else {
          toast(response.data.message.message, "warning");
        }
      })
      .catch((err) => {
        console.error(err, "leave organisation");
        toast(t("commons.genericErrorMessage"), "warning");
      });
  }

  const menuItemClasses = "flex px-4 py-2 w-full items-center";

  const menuItems = [
    ...(isAdmin
      ? [
          <div key={0} className={menuItemClasses} onClick={openMembers}>
            <IconUsers width={15} className="mr-3 !min-w-4" stroke={2.5} />
            {t("organisationSettings.manageMembers")}
          </div>,
          ...(isFeatureVisible("rules_feature") && IS_CIRCULAR_LCA
            ? [
                <div
                  className={menuItemClasses}
                  key={1}
                  onClick={openRulesEditor}
                >
                  <IconAutomation
                    width={15}
                    className="mr-3 !min-w-4"
                    stroke={2.5}
                  />
                  {t("organisationSettings.defineMappingRules")}
                </div>,
              ]
            : []),
          <div key={2} className={menuItemClasses} onClick={openSettings}>
            <IconPencil width={15} className="mr-3 !min-w-4" stroke={2.5} />
            {t("organisationSettings.editOrganisation")}
          </div>,
        ]
      : []),

    <div
      key={3}
      className={clsx(menuItemClasses, "text-red-700", {
        "opacity-50 pointer-events-none": !multipleAdmin && isAdmin,
      })}
      onClick={leaveOrganisation}
    >
      <IconLogout
        width={20}
        className="mr-3 text-red-700 !min-w-4"
        stroke={2.5}
      />
      {t("organisationSettings.leaveOrganisation")}
    </div>,
  ];

  return (
    <ActionMenu
      items={menuItems}
      itemClassName="!p-0 w-full font-medium w-fit whitespace-nowrap text-sm"
      containerClassName="-mr-5 bg-white"
    ></ActionMenu>
  );
};

export default observer(OrganisationSettingsMenu);
